import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { PlainButton, inkA, orange, tokens } from 'components/v3';
import Glyph from '../../Forms/Submissions/Glyph';
import { sx } from '../../Forms/Submissions/sx';

// A payee-detail filter (design W9Parts.FilterMenu, w9-plan §6.3 item 6):
// one pill per dropdown / radio / checkbox field in the payee slot. It
// opens a menu of the field's options with the counts on the loaded page
// and filters the rows on the page (the design does the same). An
// `allow_other` field gets one more bucket, Other, for typed values.

export const OTHER_KEY = '__other';
export const OTHER_LABEL = 'Other';

export const isFilterableField = (field) =>
  !!field &&
  ['dropdown', 'radio', 'checkbox'].includes(field.type) &&
  Array.isArray(field.config && field.config.options) &&
  field.config.options.length > 0;

export const optionsOf = (field) =>
  (field && field.config && field.config.options) || [];

const values = (answer) => {
  if (answer == null || answer === '') return [];
  return (Array.isArray(answer) ? answer : [answer]).map((v) =>
    String(v).trim()
  );
};

// Does the row's answer satisfy the filter value ('' = any)?
export const matchesFilter = (field, answer, value) => {
  if (!value) return true;
  const options = optionsOf(field);
  const list = values(answer);
  if (value === OTHER_KEY) {
    return list.some((v) => v && !options.includes(v));
  }
  return list.includes(value);
};

// { _all, [option]: n, [OTHER_KEY]: n } over the rows' answers.
export const countsFor = (field, rows) => {
  const options = optionsOf(field);
  const counts = { _all: rows.length };
  rows.forEach((row) => {
    values(row.answers && row.answers[field.id]).forEach((v) => {
      const key = options.includes(v) ? v : v ? OTHER_KEY : null;
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
  });
  return counts;
};

const Wrap = styled.div`
  position: relative;
  flex: none;
  display: inline-flex;
  align-items: center;
  height: 42px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$set || p.$open ? orange(1) : p.$border)};
  background: ${(p) => (p.$set ? orange(0.1) : p.$bg)};
  transition: border-color 120ms ease, background 120ms ease;

  &:hover {
    border-color: ${orange(1)};
  }
`;

const Trigger = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 100%;
  padding: 0 12px;
  color: ${(p) => p.$color};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  min-width: 0;
`;

const Clear = styled(PlainButton)`
  flex: none;
  width: 28px;
  height: 28px;
  margin-right: 6px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$color};

  &:hover {
    background: ${orange(0.12)};
  }
`;

const Menu = styled.div`
  position: absolute;
  left: 0;
  top: calc(100% + 6px);
  z-index: 40;
  width: 280px;
  max-width: calc(100vw - 24px);
  padding: 6px;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  box-shadow: ${(p) => p.$shadow};
`;

const Option = styled(PlainButton)`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 40px;
  padding: 0 10px;
  border-radius: 8px;
  background: ${(p) => (p.$on ? orange(0.1) : 'transparent')};
  color: ${(p) => (p.$on ? p.$onColor : p.$color)};
  font-size: 14px;
  font-weight: ${(p) => (p.$on ? 700 : 500)};
  text-align: left;

  &:hover {
    background: ${(p) => (p.$on ? orange(0.1) : p.$hover)};
  }
`;

const FilterMenu = ({ field, value, counts, onChange }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const s = sx(theme);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const key = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', down);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const pick = (next) => {
    onChange(next);
    setOpen(false);
  };
  const label = field.label || 'Field';
  const lower = label.toLowerCase();
  const options = optionsOf(field).concat(
    field.config && field.config.allow_other ? [OTHER_KEY] : []
  );
  const text = (o) => (o === OTHER_KEY ? OTHER_LABEL : o);
  const row = (o, on, name, n) => (
    <Option
      key={o || '_any'}
      role="option"
      aria-selected={on}
      $on={on}
      $onColor={s.otext}
      $color={t.text}
      $hover={s.hover}
      onClick={() => pick(o)}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: n || on ? undefined : t.faint,
        }}
      >
        {name}
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: t.faint,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {Number(n || 0).toLocaleString()}
      </span>
      {on ? <Glyph name="check" size={15} /> : null}
    </Option>
  );

  return (
    <Wrap
      ref={ref}
      $set={!!value}
      $open={open}
      $border={t.border}
      $bg={inkA(theme, 0.04)}
      data-w9-filter={field.id}
    >
      <Trigger
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Filter by ${lower}`}
        onClick={() => setOpen((o) => !o)}
        $color={value ? s.otext : t.sub}
      >
        <Glyph name="sliders-horizontal" size={14} />
        <span style={{ color: value ? s.otext : t.faint }}>{label}</span>
        <span
          style={{
            maxWidth: 170,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: value ? s.otext : t.text,
            fontWeight: 700,
          }}
        >
          {value ? text(value) : 'Any'}
        </span>
        <Glyph name="chevron-down" size={14} />
      </Trigger>
      {value ? (
        <Clear
          aria-label={`Clear ${label} filter`}
          onClick={() => onChange('')}
          $color={s.otext}
        >
          <Glyph name="x" size={13} />
        </Clear>
      ) : null}
      {open ? (
        <Menu
          role="listbox"
          aria-label={label}
          $border={s.line2}
          $bg={s.card}
          $shadow={s.pop}
        >
          <div
            style={{
              padding: '8px 10px 6px',
              fontSize: 10.5,
              fontWeight: 800,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              color: t.faint,
            }}
          >
            Filter by {lower}
          </div>
          {row('', !value, `Any ${lower}`, counts._all)}
          {options.map((o) => row(o, value === o, text(o), counts[o]))}
        </Menu>
      ) : null}
    </Wrap>
  );
};

FilterMenu.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    type: PropTypes.string,
    config: PropTypes.object,
  }).isRequired,
  value: PropTypes.string,
  counts: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

FilterMenu.defaultProps = { value: '', counts: {} };

export default FilterMenu;
